import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ArrowPathIcon,
  TrashIcon,
  EyeIcon,
  EyeSlashIcon,
  PlayIcon,
} from '@heroicons/react/24/outline';
import Card from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Checkbox from '@/components/ui/Checkbox';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import {
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableHeaderCell,
  TableCell,
  TableEmpty,
} from '@/components/ui/Table';
import { apiClient } from '@/lib/api';
import type { TTSJob } from '@/types/api';
import { getJobTitle, getJobVoice, formatDuration } from '@/types/api';

const PAGE_SIZE = 10;
const HIDDEN_JOBS_KEY = 'tts-jobs-hidden';
const ACTIVE_JOB_STATUSES = new Set(['pending', 'running', 'processing']);

interface LiveJobStatusResponse {
  status?: TTSJob['status'];
  current_step?: string | null;
  pipeline_progress?: {
    percentage?: number;
  };
}

interface JobBatchesResponse {
  total?: number;
  completed?: number;
}

function asNonNegativeNumber(value: unknown): number {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  return Math.max(0, numeric);
}

function normalizeProgress(value: unknown): number {
  return Math.min(100, asNonNegativeNumber(value));
}

function loadHiddenJobIds(): string[] {
  try {
    const raw = localStorage.getItem(HIDDEN_JOBS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveHiddenJobIds(ids: string[]) {
  localStorage.setItem(HIDDEN_JOBS_KEY, JSON.stringify(ids));
}

const statusOptions = [
  { value: '', label: 'All Status' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'processing', label: 'Processing' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

function getStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'running':
    case 'processing':
      return <Badge variant="primary" dot>Running</Badge>;
    case 'pending':
      return <Badge variant="warning" dot>Pending</Badge>;
    case 'failed':
      return <Badge variant="danger" dot>Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
}

function formatDate(dateStr: string) {
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getJobDuration(job: TTSJob): string {
  if (!job.finished_at || !job.created_at) return '--';
  const start = new Date(job.created_at).getTime();
  const end = new Date(job.finished_at).getTime();
  return formatDuration(end - start);
}

export default function TTSJobsPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedJobs, setSelectedJobs] = useState<string[]>([]);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Real data state
  const [jobs, setJobs] = useState<TTSJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null);
  const [hiddenJobIds, setHiddenJobIds] = useState<string[]>(loadHiddenJobIds);

  const handleHideJob = useCallback((jobId: string) => {
    setHiddenJobIds((prev) => {
      const next = [...prev, jobId];
      saveHiddenJobIds(next);
      return next;
    });
    setSelectedJobs((prev) => prev.filter((id) => id !== jobId));
  }, []);

  const handleClearHidden = useCallback(() => {
    setHiddenJobIds([]);
    saveHiddenJobIds([]);
  }, []);

  // Extract unique voices from jobs for filter
  const voiceOptions = useMemo(() => {
    const voices = new Set<string>();
    jobs.forEach((job) => {
      const voice = getJobVoice(job);
      if (voice && voice !== 'Unknown') voices.add(voice);
    });
    return [
      { value: '', label: 'All Voices' },
      ...Array.from(voices).map((v) => ({ value: v, label: v })),
    ];
  }, [jobs]);

  const [voiceFilter, setVoiceFilter] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [minProgress, setMinProgress] = useState('');

  // Fetch jobs from backend
  const fetchJobs = useCallback(async (showLoading = true) => {
    try {
      if (showLoading) setIsLoading(true);
      else setIsRefreshing(true);

      const response = await apiClient.get<TTSJob[]>('tts/jobs');
      const activeJobs = response.filter((job) => ACTIVE_JOB_STATUSES.has(job.status)).slice(0, PAGE_SIZE);

      if (activeJobs.length === 0) {
        setJobs(response);
        setError(null);
        return;
      }

      const liveStatusEntries = await Promise.all(
        activeJobs.map(async (job) => {
          try {
            const [liveStatus, batchStatus] = await Promise.all([
              apiClient.get<LiveJobStatusResponse>(`tts/jobs/${job.job_id}/status`),
              apiClient.get<JobBatchesResponse>(`tts/jobs/${job.job_id}/batches`).catch(() => null),
            ]);
            return [job.job_id, { liveStatus, batchStatus }] as const;
          } catch {
            return null;
          }
        })
      );

      const liveStatusByJobId = new Map(
        liveStatusEntries.filter((
          entry
        ): entry is readonly [string, { liveStatus: LiveJobStatusResponse; batchStatus: JobBatchesResponse | null }] => entry !== null)
      );

      const mergedJobs = response.map((job) => {
        const liveData = liveStatusByJobId.get(job.job_id);
        if (!liveData) {
          return job;
        }

        const { liveStatus, batchStatus } = liveData;
        const pipelineProgress = normalizeProgress(liveStatus.pipeline_progress?.percentage ?? job.progress ?? 0);
        const totalBatches = asNonNegativeNumber(batchStatus?.total ?? 0);
        const completedBatches = asNonNegativeNumber(batchStatus?.completed ?? 0);
        const batchProgress = totalBatches > 0 ? normalizeProgress((completedBatches / totalBatches) * 100) : 0;
        const mergedProgress = normalizeProgress(Math.max(normalizeProgress(job.progress ?? 0), pipelineProgress, batchProgress));

        return {
          ...job,
          status: liveStatus.status || job.status,
          status_message: liveStatus.current_step || job.status_message,
          progress: mergedProgress,
        };
      });

      setJobs(mergedJobs);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  const handleDeleteJob = useCallback(
    async (jobId: string) => {
      const confirmed = window.confirm(
        `Delete job ${jobId}? This action cannot be undone.`
      );
      if (!confirmed) return;

      try {
        setDeletingJobId(jobId);
        await apiClient.delete<{ success?: boolean; error?: string }>(
          `tts/jobs/${jobId}`,
          {
            searchParams: { delete_files: '1' },
          }
        );

        // Remove from current selection
        setSelectedJobs((prev) => prev.filter((id) => id !== jobId));

        // Refresh jobs list (stats are computed from jobs array)
        await fetchJobs(false);
      } catch (err) {
        console.error('Failed to delete job:', err);
        const hideInstead = window.confirm(
          'Failed to delete job. Would you like to hide it from the list instead?'
        );
        if (hideInstead) handleHideJob(jobId);
        else window.alert('Failed to delete job. Please try again.');
      } finally {
        setDeletingJobId(null);
      }
    },
    [fetchJobs, handleHideJob]
  );

  // Initial data fetch
  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchJobs(false);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchJobs]);

  // Filter jobs based on search, status, voice, advanced filters, and hidden
  const filteredJobs = useMemo(() => {
    return jobs.filter((job) => {
      if (hiddenJobIds.includes(job.job_id)) return false;
      const title = getJobTitle(job);
      const voice = getJobVoice(job);
      const progress = Number(job.progress ?? 0);
      const createdAt = new Date(job.created_at);

      const matchesSearch =
        title.toLowerCase().includes(search.toLowerCase()) ||
        job.job_id.toLowerCase().includes(search.toLowerCase());
      const matchesStatus = !statusFilter || job.status === statusFilter;
      const matchesVoice = !voiceFilter || voice === voiceFilter;
      let matchesDateRange = true;
      if (createdFrom) {
        const from = new Date(createdFrom);
        if (!Number.isNaN(from.getTime())) {
          // Normalize to start of the day for "from"
          from.setHours(0, 0, 0, 0);
          matchesDateRange = matchesDateRange && createdAt >= from;
        }
      }
      if (createdTo) {
        const to = new Date(createdTo);
        if (!Number.isNaN(to.getTime())) {
          // Include the full "to" day
          to.setHours(23, 59, 59, 999);
          matchesDateRange = matchesDateRange && createdAt <= to;
        }
      }

      let matchesProgress = true;
      const minProgressNumber = Number(minProgress);
      if (!Number.isNaN(minProgressNumber) && minProgressNumber > 0) {
        matchesProgress = progress >= minProgressNumber;
      }

      return (
        matchesSearch &&
        matchesStatus &&
        matchesVoice &&
        matchesDateRange &&
        matchesProgress
      );
    });
  }, [jobs, hiddenJobIds, search, statusFilter, voiceFilter, createdFrom, createdTo, minProgress]);

  // Reset to first page whenever filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [search, statusFilter, voiceFilter, createdFrom, createdTo, minProgress]);

  const totalPages = filteredJobs.length === 0 ? 1 : Math.ceil(filteredJobs.length / PAGE_SIZE);

  const paginatedJobs = useMemo(() => {
    const safePage = Math.min(Math.max(currentPage, 1), totalPages || 1);
    const start = (safePage - 1) * PAGE_SIZE;
    const end = start + PAGE_SIZE;
    return filteredJobs.slice(start, end);
  }, [filteredJobs, currentPage, totalPages]);

  const toggleJob = (jobId: string) => {
    setSelectedJobs((prev) =>
      prev.includes(jobId) ? prev.filter((id) => id !== jobId) : [...prev, jobId]
    );
  };

  const toggleAll = () => {
    const pageJobIds = paginatedJobs.map((j) => j.job_id);
    const allSelectedOnPage = pageJobIds.every((id) => selectedJobs.includes(id));

    if (allSelectedOnPage) {
      setSelectedJobs((prev) => prev.filter((id) => !pageJobIds.includes(id)));
      return;
    }

    setSelectedJobs((prev) => Array.from(new Set([...prev, ...pageJobIds])));
  };

  // Stats computed from visible jobs (excludes hidden)
  const visibleJobs = jobs.filter((j) => !hiddenJobIds.includes(j.job_id));
  const totalJobs = visibleJobs.length;
  const pendingCount = visibleJobs.filter((j) => j.status === 'pending').length;
  const runningCount = visibleJobs.filter((j) => j.status === 'running' || j.status === 'processing').length;
  const failedCount = visibleJobs.filter((j) => j.status === 'failed').length;

  const startIndex = filteredJobs.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endIndex = Math.min(currentPage * PAGE_SIZE, filteredJobs.length);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Conversion Jobs</h1>
          <p className="text-slate-600 dark:text-slate-300 mt-1">Manage and monitor your TTS conversion jobs</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            leftIcon={<ArrowPathIcon className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />}
            onClick={() => fetchJobs(false)}
            disabled={isRefreshing}
          >
            Refresh
          </Button>
          <Link to="/tts/upload">
            <Button leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}>
              New Upload
            </Button>
          </Link>
        </div>
      </div>

      {/* Filters */}
      <Card padding="md">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1">
            <Input
              placeholder="Search jobs..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <div className="flex flex-wrap gap-3">
            <Select
              options={statusOptions}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            />
            <Select
              options={voiceOptions}
              value={voiceFilter}
              onChange={(e) => setVoiceFilter(e.target.value)}
              className="w-48"
            />
            <Button
              variant={showAdvancedFilters ? 'primary' : 'outline'}
              leftIcon={<FunnelIcon className="w-4 h-4" />}
              onClick={() => setShowAdvancedFilters((prev) => !prev)}
            >
              {showAdvancedFilters ? 'Hide Filters' : 'More Filters'}
            </Button>
            {hiddenJobIds.length > 0 && (
              <Button variant="ghost" size="sm" onClick={handleClearHidden}>
                Clear {hiddenJobIds.length} hidden
              </Button>
            )}
          </div>
        </div>
        {showAdvancedFilters && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Created from</p>
              <input
                type="date"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Created to</p>
              <input
                type="date"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-1">Minimum progress (%)</p>
              <input
                type="number"
                min={0}
                max={100}
                value={minProgress}
                onChange={(e) => setMinProgress(e.target.value)}
                className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 placeholder:text-slate-400 dark:placeholder:text-slate-500 px-3 py-2 text-sm"
                placeholder="e.g. 50"
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setCreatedFrom('');
                  setCreatedTo('');
                  setMinProgress('');
                }}
              >
                Clear advanced filters
              </Button>
            </div>
          </div>
        )}
      </Card>

      {/* Stats Summary - Connected to Real Data */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40} showSpinner={false}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">Total Jobs</p>
            <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{totalJobs}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">Pending</p>
            <p className="text-2xl font-bold text-warning-500">{pendingCount}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">Running</p>
            <p className="text-2xl font-bold text-primary-500">{runningCount}</p>
          </div>
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
            <p className="text-sm text-slate-600 dark:text-slate-300">Failed</p>
            <p className="text-2xl font-bold text-danger-500">{failedCount}</p>
          </div>
        </div>
      </LoadingOverlay>

      {/* Jobs Table */}
      <LoadingOverlay isLoading={isLoading} loadingOpacity={40}>
        <Card padding="none">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow isHoverable={false}>
                  <TableHeaderCell className="w-12">
                    <Checkbox
                      checked={
                        paginatedJobs.length > 0 &&
                        paginatedJobs.every((job) => selectedJobs.includes(job.job_id))
                      }
                      onChange={toggleAll}
                    />
                  </TableHeaderCell>
                  <TableHeaderCell>Job</TableHeaderCell>
                  <TableHeaderCell>Voice</TableHeaderCell>
                  <TableHeaderCell>Progress</TableHeaderCell>
                  <TableHeaderCell>Status</TableHeaderCell>
                  <TableHeaderCell>Duration</TableHeaderCell>
                  <TableHeaderCell>Created</TableHeaderCell>
                  <TableHeaderCell className="w-32">Actions</TableHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {error ? (
                  <TableEmpty
                    message="Failed to load jobs"
                    description={error}
                    colSpan={8}
                  />
                ) : filteredJobs.length === 0 && !isLoading ? (
                  <TableEmpty
                    message="No jobs found"
                    description="Try adjusting your filters or upload a new file"
                    colSpan={8}
                  />
                ) : (
                  paginatedJobs.map((job) => (
                    <TableRow key={job.id} isSelected={selectedJobs.includes(job.job_id)}>
                      <TableCell>
                        <Checkbox
                          checked={selectedJobs.includes(job.job_id)}
                          onChange={() => toggleJob(job.job_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <Link
                          to={`/tts/jobs/${job.job_id}`}
                          className="hover:text-primary-500 transition-colors"
                        >
                          <p className="font-medium text-slate-900 dark:text-slate-100">{getJobTitle(job)}</p>
                          <p className="text-xs text-slate-500 dark:text-slate-400 font-mono">{job.job_id}</p>
                        </Link>
                      </TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">{getJobVoice(job)}</TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden max-w-[80px]">
                            <div
                              className={`h-full rounded-full transition-all ${
                                job.status === 'failed'
                                  ? 'bg-red-500'
                                  : job.status === 'completed'
                                  ? 'bg-emerald-500'
                                  : 'bg-primary-500'
                              }`}
                              style={{ width: `${job.progress}%` }}
                            />
                          </div>
                          <span className="text-xs">{job.progress}%</span>
                        </div>
                      </TableCell>
                      <TableCell>{getStatusBadge(job.status)}</TableCell>
                      <TableCell className="text-slate-700 dark:text-slate-200">
                        {getJobDuration(job)}
                      </TableCell>
                      <TableCell className="text-slate-600 dark:text-slate-300 text-sm">
                        {formatDate(job.created_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Link to={`/tts/jobs/${job.job_id}`}>
                            <Button variant="ghost" size="sm" className="p-2">
                              <EyeIcon className="w-4 h-4" />
                            </Button>
                          </Link>
                          {job.status === 'completed' && (
                            <Link to={`/tts/player/${job.job_id}`}>
                              <Button variant="ghost" size="sm" className="p-2">
                                <PlayIcon className="w-4 h-4" />
                              </Button>
                            </Link>
                          )}
                          {job.status === 'failed' && (
                            <Button variant="ghost" size="sm" className="p-2">
                              <ArrowPathIcon className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2"
                            onClick={() => handleHideJob(job.job_id)}
                            aria-label={`Hide job ${job.job_id} from list`}
                            title="Hide from list"
                          >
                            <EyeSlashIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="p-2 text-danger-500 hover:text-danger-600"
                            onClick={() => handleDeleteJob(job.job_id)}
                            disabled={deletingJobId === job.job_id}
                            aria-label={`Delete job ${job.job_id}`}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Bulk Actions */}
          {selectedJobs.length > 0 && (
            <div className="px-4 py-3 bg-slate-50 dark:bg-slate-900/60 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <p className="text-sm text-slate-700 dark:text-slate-200">
                {selectedJobs.length} job{selectedJobs.length > 1 ? 's' : ''} selected
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<EyeSlashIcon className="w-4 h-4" />}
                  onClick={() => {
                    setHiddenJobIds((prev) => {
                      const next = [...new Set([...prev, ...selectedJobs])];
                      saveHiddenJobIds(next);
                      return next;
                    });
                    setSelectedJobs([]);
                  }}
                >
                  Hide selected
                </Button>
                <Button variant="outline" size="sm" leftIcon={<ArrowPathIcon className="w-4 h-4" />}>
                  Retry Failed
                </Button>
                <Button variant="danger" size="sm" leftIcon={<TrashIcon className="w-4 h-4" />}>
                  Delete Selected
                </Button>
              </div>
            </div>
          )}
        </Card>
      </LoadingOverlay>

      {/* Pagination */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {filteredJobs.length === 0
            ? 'No jobs to display'
            : `Showing ${startIndex}-${endIndex} of ${filteredJobs.length} job${
                filteredJobs.length > 1 ? 's' : ''
              }`}
        </p>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage <= 1 || filteredJobs.length === 0}
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="bg-primary-50 border-primary-200 text-primary-600"
            disabled={filteredJobs.length === 0}
          >
            Page {filteredJobs.length === 0 ? 0 : currentPage} of {filteredJobs.length === 0 ? 0 : totalPages}
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={currentPage >= totalPages || filteredJobs.length === 0}
            onClick={() =>
              setCurrentPage((prev) => Math.min(totalPages, prev + 1))
            }
          >
            Next
          </Button>
        </div>
      </div>
    </div>
  );
}
